/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ChannelActionCreators from '@app/actions/ChannelActionCreators';
import * as InviteActionCreators from '@app/actions/InviteActionCreators';
import * as LayoutActionCreators from '@app/actions/LayoutActionCreators';
import * as MessageActionCreators from '@app/actions/MessageActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import * as NavigationActionCreators from '@app/actions/NavigationActionCreators';
import * as PrivateChannelActionCreators from '@app/actions/PrivateChannelActionCreators';
import * as ReadStateActionCreators from '@app/actions/ReadStateActionCreators';
import * as TextCopyActionCreators from '@app/actions/TextCopyActionCreators';
import * as ToastActionCreators from '@app/actions/ToastActionCreators';
import * as UserGuildSettingsActionCreators from '@app/actions/UserGuildSettingsActionCreators';
import * as UserProfileActionCreators from '@app/actions/UserProfileActionCreators';
import {DMCloseFailedModal} from '@app/components/alerts/DMCloseFailedModal';
import type {InviteCandidate} from '@app/components/channel/direct_message/DMListHelpers';
import {createMuteConfig} from '@app/components/channel/MuteOptions';
import {ChangeFriendNicknameModal} from '@app/components/modals/ChangeFriendNicknameModal';
import {ConfirmModal} from '@app/components/modals/ConfirmModal';
import {EditGroupModal} from '@app/components/modals/EditGroupModal';
import {GroupInvitesModal} from '@app/components/modals/GroupInvitesModal';
import {Logger} from '@app/lib/Logger';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {UserRecord} from '@app/records/UserRecord';
import FavoritesStore from '@app/stores/FavoritesStore';
import MobileLayoutStore from '@app/stores/MobileLayoutStore';
import RuntimeConfigStore from '@app/stores/RuntimeConfigStore';
import SelectedChannelStore from '@app/stores/SelectedChannelStore';
import * as CallUtils from '@app/utils/CallUtils';
import * as RelationshipActionUtils from '@app/utils/RelationshipActionUtils';
import {ME} from '@fluxer/constants/src/AppConstants';
import {RelationshipTypes} from '@fluxer/constants/src/UserConstants';
import {fromTimestamp} from '@fluxer/snowflake/src/SnowflakeUtils';
import type {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useCallback} from 'react';

const logger = new Logger('DMListItem');

interface UseDMListItemHandlersParams {
	channel: ChannelRecord;
	recipient: UserRecord | null;
	isGroupDM: boolean;
	isMobile: boolean;
	relationshipType: number | undefined;
	restrictRecipientActions: boolean;
	closeAllSheets: () => void;
	setMenuOpen: (open: boolean) => void;
	setEditGroupSheetOpen: (open: boolean) => void;
	setInvitesSheetOpen: (open: boolean) => void;
	leaveGroup: (channelId: string) => void;
	i18n: I18n;
}

export function useDMListItemHandlers({
	channel,
	recipient,
	isGroupDM,
	isMobile,
	relationshipType,
	restrictRecipientActions,
	closeAllSheets,
	setMenuOpen,
	setEditGroupSheetOpen,
	setInvitesSheetOpen,
	leaveGroup,
	i18n,
}: UseDMListItemHandlersParams) {
	const {t} = useLingui();

	const navigateTo = useCallback(() => {
		NavigationActionCreators.selectChannel(ME, channel.id);
		if (MobileLayoutStore.isMobileLayout()) {
			LayoutActionCreators.updateMobileLayoutState(false, true);
		}
	}, [channel.id]);

	const handleRemoveChannel = useCallback(
		(e?: React.MouseEvent | React.KeyboardEvent) => {
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
			setMenuOpen(false);

			if (isGroupDM) {
				leaveGroup(channel.id);
				return;
			}

			ChannelActionCreators.remove(channel.id);
			const selectedChannel = SelectedChannelStore.selectedChannelIds.get(ME);
			if (selectedChannel === channel.id) {
				NavigationActionCreators.deselectGuild();
			}
		},
		[channel.id, isGroupDM, leaveGroup, setMenuOpen],
	);

	const handleCopyChannelId = useCallback(async () => {
		await TextCopyActionCreators.copy(i18n, channel.id);
		closeAllSheets();
	}, [channel.id, closeAllSheets, i18n]);

	const handleCopyUserId = useCallback(async () => {
		if (!recipient) return;
		await TextCopyActionCreators.copy(i18n, recipient.id);
		closeAllSheets();
	}, [recipient, closeAllSheets, i18n]);

	const handleMarkAsRead = useCallback(() => {
		ReadStateActionCreators.ack(channel.id, true, true);
		closeAllSheets();
	}, [channel.id, closeAllSheets]);

	const handleCloseDm = useCallback(() => {
		closeAllSheets();

		const username = recipient?.username ?? '';
		const description = isGroupDM
			? t`Are you sure you want to close this group DM? You can always reopen it later.`
			: t`Are you sure you want to close your DM with ${username}? You can always reopen it later.`;

		ModalActionCreators.push(
			modal(() => (
				<ConfirmModal
					title={t`Close DM`}
					description={description}
					primaryText={t`Close DM`}
					primaryVariant="danger-primary"
					onPrimary={async () => {
						try {
							await ChannelActionCreators.remove(channel.id);

							const selectedChannel = SelectedChannelStore.selectedChannelIds.get(ME);
							if (selectedChannel === channel.id) {
								NavigationActionCreators.deselectGuild();
							}

							ToastActionCreators.createToast({
								type: 'success',
								children: t`DM closed`,
							});
						} catch (error) {
							logger.error('Failed to close DM:', error);
							window.setTimeout(() => {
								ModalActionCreators.push(modal(() => <DMCloseFailedModal />));
							}, 0);
						}
					}}
				/>
			)),
		);
	}, [channel.id, closeAllSheets, isGroupDM, recipient?.username, t]);

	const handleViewProfile = useCallback(() => {
		if (!recipient) return;
		closeAllSheets();
		UserProfileActionCreators.openUserProfile(recipient.id);
	}, [recipient, closeAllSheets]);

	const handleStartVoiceCall = useCallback(async () => {
		if (!recipient || recipient.bot) return;
		closeAllSheets();
		try {
			const dmChannelId = await PrivateChannelActionCreators.ensureDMChannel(recipient.id);
			await CallUtils.requestStartCall(i18n, dmChannelId, {kind: 'voice'});
		} catch (error) {
			logger.error('Failed to start voice call:', error);
		}
	}, [closeAllSheets, i18n, recipient]);

	const handleAddNote = useCallback(() => {
		if (!recipient || recipient.bot || restrictRecipientActions) return;
		closeAllSheets();
		UserProfileActionCreators.openUserProfile(recipient.id, undefined, true);
	}, [recipient, restrictRecipientActions, closeAllSheets]);

	const handleChangeFriendNickname = useCallback(() => {
		if (!recipient || relationshipType !== RelationshipTypes.FRIEND) return;
		closeAllSheets();
		ModalActionCreators.push(modal(() => <ChangeFriendNicknameModal user={recipient} />));
	}, [recipient, relationshipType, closeAllSheets]);

	const handleMute = useCallback(
		(duration: number | null) => {
			closeAllSheets();
			UserGuildSettingsActionCreators.updateChannelOverride(
				null,
				channel.id,
				{
					muted: true,
					mute_config: duration ? createMuteConfig(duration) : null,
				},
				{persistImmediately: true},
			);
		},
		[channel.id, closeAllSheets],
	);

	const handleUnmute = useCallback(() => {
		closeAllSheets();
		UserGuildSettingsActionCreators.updateChannelOverride(
			null,
			channel.id,
			{
				muted: false,
				mute_config: null,
			},
			{persistImmediately: true},
		);
	}, [channel.id, closeAllSheets]);

	const handleAddToFavorites = useCallback(
		(categoryId: string | null) => {
			closeAllSheets();
			const guildId = channel.guildId ?? ME;
			FavoritesStore.addChannel(channel.id, guildId, categoryId);
			ToastActionCreators.createToast({type: 'success', children: t`Channel added to favorites`});
		},
		[channel.id, channel.guildId, closeAllSheets, t],
	);

	const handleRemoveFromFavorites = useCallback(() => {
		closeAllSheets();
		FavoritesStore.removeChannel(channel.id);
		ToastActionCreators.createToast({type: 'success', children: t`Channel removed from favorites`});
	}, [channel.id, closeAllSheets, t]);

	const handleSendFriendRequest = useCallback(async () => {
		if (!recipient) return;
		closeAllSheets();
		await RelationshipActionUtils.sendFriendRequest(i18n, recipient.id);
	}, [recipient, closeAllSheets, i18n]);

	const handleAcceptFriendRequest = useCallback((event?: {shiftKey?: boolean}) => {
		if (!recipient) return;
		closeAllSheets();
		RelationshipActionUtils.showAcceptFriendRequestConfirmation(i18n, recipient, {
			bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
		});
	}, [recipient, closeAllSheets, i18n]);

	const handleIgnoreFriendRequest = useCallback(() => {
		if (!recipient) return;
		closeAllSheets();
		RelationshipActionUtils.ignoreFriendRequest(i18n, recipient.id);
	}, [recipient, closeAllSheets, i18n]);

	const handleRemoveFriend = useCallback((event?: {shiftKey?: boolean}) => {
		if (!recipient) return;
		closeAllSheets();
		RelationshipActionUtils.showRemoveFriendConfirmation(i18n, recipient, {
			bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
		});
	}, [recipient, closeAllSheets, i18n]);

	const handleBlockUser = useCallback((event?: {shiftKey?: boolean}) => {
		if (!recipient) return;
		closeAllSheets();
		RelationshipActionUtils.showBlockUserConfirmation(i18n, recipient, {
			bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
		});
	}, [recipient, closeAllSheets, i18n]);

	const handleUnblockUser = useCallback((event?: {shiftKey?: boolean}) => {
		if (!recipient) return;
		closeAllSheets();
		RelationshipActionUtils.showUnblockUserConfirmation(i18n, recipient, {
			bypassConfirm: RelationshipActionUtils.shouldBypassRelationshipConfirmation(event),
		});
	}, [recipient, closeAllSheets, i18n]);

	const handleSendInvite = useCallback(
		async (candidate: InviteCandidate) => {
			closeAllSheets();
			if (!recipient) return;
			try {
				const invite = await InviteActionCreators.create(candidate.channelId);
				const inviteUrl = `${RuntimeConfigStore.inviteEndpoint}/${invite.code}`;
				const dmChannelId = await PrivateChannelActionCreators.ensureDMChannel(recipient.id);
				await MessageActionCreators.send(dmChannelId, {
					content: inviteUrl,
					nonce: fromTimestamp(Date.now()),
				});
				ToastActionCreators.createToast({
					type: 'success',
					children: t`Invite sent for ${candidate.guild.name}`,
				});
			} catch (error) {
				logger.error('Failed to send invite via DM sheet:', error);
				ToastActionCreators.createToast({type: 'error', children: t`Failed to send invite. Please try again.`});
			}
		},
		[recipient, closeAllSheets, t],
	);

	const handleEditGroup = useCallback(() => {
		setMenuOpen(false);
		if (isMobile) {
			setEditGroupSheetOpen(true);
		} else {
			ModalActionCreators.push(modal(() => <EditGroupModal channelId={channel.id} />));
		}
	}, [channel.id, isMobile, setMenuOpen, setEditGroupSheetOpen]);

	const handleShowInvites = useCallback(() => {
		setMenuOpen(false);
		if (isMobile) {
			setInvitesSheetOpen(true);
		} else {
			ModalActionCreators.push(modal(() => <GroupInvitesModal channelId={channel.id} />));
		}
	}, [channel.id, isMobile, setMenuOpen, setInvitesSheetOpen]);

	const handleLeaveGroup = useCallback(() => {
		setMenuOpen(false);
		leaveGroup(channel.id);
	}, [channel.id, leaveGroup, setMenuOpen]);

	const handlePinChannel = useCallback(async () => {
		closeAllSheets();
		try {
			await PrivateChannelActionCreators.pinDmChannel(channel.id);
			ToastActionCreators.createToast({type: 'success', children: t`Pinned DM`});
		} catch {
			ToastActionCreators.createToast({type: 'error', children: t`Failed to pin DM`});
		}
	}, [channel.id, closeAllSheets, t]);

	const handleUnpinChannel = useCallback(async () => {
		closeAllSheets();
		try {
			await PrivateChannelActionCreators.unpinDmChannel(channel.id);
			ToastActionCreators.createToast({type: 'success', children: t`Unpinned DM`});
		} catch {
			ToastActionCreators.createToast({type: 'error', children: t`Failed to unpin DM`});
		}
	}, [channel.id, closeAllSheets, t]);

	return {
		navigateTo,
		handleRemoveChannel,
		handleCopyChannelId,
		handleCopyUserId,
		handleMarkAsRead,
		handleCloseDm,
		handleViewProfile,
		handleStartVoiceCall,
		handleAddNote,
		handleChangeFriendNickname,
		handleMute,
		handleUnmute,
		handleAddToFavorites,
		handleRemoveFromFavorites,
		handleSendFriendRequest,
		handleAcceptFriendRequest,
		handleIgnoreFriendRequest,
		handleRemoveFriend,
		handleBlockUser,
		handleUnblockUser,
		handleSendInvite,
		handleEditGroup,
		handleShowInvites,
		handleLeaveGroup,
		handlePinChannel,
		handleUnpinChannel,
	};
}
